import {computed, onMounted, onUnmounted, ref} from 'vue'
import {listen} from '@tauri-apps/api/event'
import {invoke} from '@tauri-apps/api/core'

export function useNetworkMonitor() {
    const isOnline = ref(true)
    const hasInternet = ref(true)
    const latencyMs = ref(null)
    const connectionType = ref('Unknown')
    const lastConnectionLost = ref(null)
    const lastConnectionRestored = ref(null)

    let unlistenNetworkStatus = null
    let unlistenNetworkLost = null
    let unlistenNetworkRestored = null
    let browserCleanup = null

    const connectionStatus = computed(() => {
        if (!isOnline.value || !hasInternet.value) return 'offline'
        if (latencyMs.value === null) return 'checking'
        if (latencyMs.value < 100) return 'excellent'
        if (latencyMs.value < 300) return 'good'
        if (latencyMs.value < 600) return 'fair'
        return 'poor'
    })

    const statusColor = computed(() => {
        switch (connectionStatus.value) {
            case 'offline':
                return 'error'
            case 'excellent':
            case 'good':
                return 'success'
            case 'fair':
                return 'warning'
            case 'poor':
                return 'orange'
            case 'checking':
                return 'info'
            default:
                return 'grey'
        }
    })

    const statusIcon = computed(() => {
        switch (connectionStatus.value) {
            case 'offline':
                return 'mdi mdi-wifi-off'
            case 'excellent':
            case 'good':
                return 'mdi mdi-wifi'
            case 'fair':
                return 'mdi mdi-wifi-strength-2'
            case 'poor':
                return 'mdi mdi-wifi-strength-1'
            case 'checking':
                return 'mdi mdi-wifi-sync'
            default:
                return 'mdi mdi-wifi-strength-outline'
        }
    })

    const statusText = computed(() => {
        if (!isOnline.value || !hasInternet.value) {
            return 'Offline'
        }
        if (latencyMs.value !== null) {
            return `${latencyMs.value}ms`
        }
        return 'Checking...'
    })

    const checkInternetConnectivity = async () => {
        const startTime = performance.now()
        let controller = new AbortController()
        let timeoutId = setTimeout(() => controller.abort(), 5000)

        try {
            const response = await fetch('https://clients3.google.com/generate_204', {
                method: 'GET',
                cache: 'no-cache',
                signal: controller.signal
            })

            clearTimeout(timeoutId)
            const latency = Math.round(performance.now() - startTime)
            return { hasInternet: response.status === 204 || response.ok, latency }
        } catch (err) {
            clearTimeout(timeoutId)

            const fallbackStart = performance.now()
            controller = new AbortController()
            timeoutId = setTimeout(() => controller.abort(), 5000)

            try {
                await fetch('https://httpbin.org/get', {
                    method: 'HEAD',
                    cache: 'no-cache',
                    signal: controller.signal
                })

                clearTimeout(timeoutId)
                const latency = Math.round(performance.now() - fallbackStart)
                return { hasInternet: true, latency }
            } catch (fallbackErr) {
                clearTimeout(timeoutId)
                return { hasInternet: navigator.onLine, latency: navigator.onLine ? 100 : null }
            }
        }
    }

    const getNetworkStatus = async () => {
        try {
            const status = await invoke('get_network_status')
            isOnline.value = status.is_online
            hasInternet.value = status.has_internet
            latencyMs.value = status.latency_ms
            connectionType.value = status.connection_type
        } catch (err) {
            console.log('[Network Monitor] Tauri command not available, using browser API fallback')
            isOnline.value = navigator.onLine

            if (navigator.onLine) {
                const { hasInternet: connected, latency } = await checkInternetConnectivity()
                hasInternet.value = connected
                latencyMs.value = latency
            } else {
                hasInternet.value = false
                latencyMs.value = null
            }

            connectionType.value = 'Unknown'
        }
    }

    const initListeners = async () => {
        try {
            unlistenNetworkStatus = await listen('network-status', (event) => {
                const status = event.payload
                isOnline.value = status.is_online
                hasInternet.value = status.has_internet
                latencyMs.value = status.latency_ms
                connectionType.value = status.connection_type
            })

            unlistenNetworkLost = await listen('network-lost', (event) => {
                console.log('[Network Monitor] ❌ Connection lost')
                lastConnectionLost.value = new Date()
                showConnectionLostNotification()
            })

            unlistenNetworkRestored = await listen('network-restored', (event) => {
                const status = event.payload
                console.log('[Network Monitor] ✅ Connection restored - Latency:', status.latency_ms, 'ms')
                lastConnectionRestored.value = new Date()
                showConnectionRestoredNotification(status)
            })

            console.log('[Network Monitor] Tauri event listeners initialized')
        } catch (err) {
            console.log('[Network Monitor] Tauri events not available, using browser events')

            const handleOnline = async () => {
                isOnline.value = true
                const { hasInternet: connected, latency } = await checkInternetConnectivity()
                hasInternet.value = connected
                latencyMs.value = latency
                lastConnectionRestored.value = new Date()
                console.log('[Network Monitor] ✅ Browser: Connection restored -', latency, 'ms')
            }

            const handleOffline = () => {
                isOnline.value = false
                hasInternet.value = false
                latencyMs.value = null
                lastConnectionLost.value = new Date()
                console.log('[Network Monitor] ❌ Browser: Connection lost')
            }

            window.addEventListener('online', handleOnline)
            window.addEventListener('offline', handleOffline)

            let periodicCheckInterval = setInterval(async () => {
                if (navigator.onLine) {
                    const { hasInternet: connected, latency } = await checkInternetConnectivity()
                    hasInternet.value = connected
                    latencyMs.value = latency
                }
            }, 30000)

            browserCleanup = () => {
                window.removeEventListener('online', handleOnline)
                window.removeEventListener('offline', handleOffline)
                if (periodicCheckInterval) {
                    clearInterval(periodicCheckInterval)
                }
            }
        }
    }

    const showConnectionLostNotification = () => {
        if (window.$notify) {
            window.$notify({
                title: 'Network Disconnected',
                message: 'Internet connection lost. The app will continue in offline mode.',
                type: 'warning',
                duration: 5000,
            })
        }
    }

    const showConnectionRestoredNotification = (status) => {
        if (window.$notify) {
            window.$notify({
                title: 'Network Restored',
                message: `Internet connection restored. Latency: ${status.latency_ms}ms`,
                type: 'success',
                duration: 3000,
            })
        }
    }

    const cleanup = () => {
        if (unlistenNetworkStatus) {
            unlistenNetworkStatus()
            unlistenNetworkStatus = null
        }
        if (unlistenNetworkLost) {
            unlistenNetworkLost()
            unlistenNetworkLost = null
        }
        if (unlistenNetworkRestored) {
            unlistenNetworkRestored()
            unlistenNetworkRestored = null
        }
        if (browserCleanup) {
            browserCleanup()
            browserCleanup = null
        }
    }

    const init = async () => {
        await getNetworkStatus()
        await initListeners()
    }

    if (typeof onMounted !== 'undefined') {
        try {
            onMounted(() => {
                init()
            })

            onUnmounted(() => {
                cleanup()
            })
        } catch (err) {
            console.warn('[Network Monitor] Lifecycle hooks not available, call init() manually')
        }
    }

    return {
        isOnline,
        hasInternet,
        latencyMs,
        connectionType,
        connectionStatus,
        statusColor,
        statusIcon,
        statusText,
        lastConnectionLost,
        lastConnectionRestored,
        getNetworkStatus,
        init,
        cleanup,
    }
}
